import { useEffect, useRef, useState } from "react";
import { Sparkles, ExternalLink } from "lucide-react";
import { briefingApi, type BriefingResponse, type BriefingContent } from "@/lib/api";

/**
 * Trip Intel briefing card. Polls the async briefing while it generates, shows it
 * prominently the first time (fresh), stamps it seen, then renders as a quiet
 * collapsed disclosure on subsequent visits ("old news"). Renders nothing while
 * there's no usable briefing (none/failed) so it never clutters the page.
 */
export default function TripBriefing({ tripId }: { tripId: string }) {
  const [data, setData] = useState<BriefingResponse | null>(null);
  const seenSent = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = async () => {
      try {
        const d = await briefingApi.get(tripId);
        if (cancelled) return;
        setData(d);
        if (d.status === "pending" && tries++ < 30) timer = setTimeout(poll, 4000);
      } catch {
        /* keep whatever we have; the card just won't show */
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [tripId]);

  // Stamp seen once, the first time a ready+unseen briefing is shown.
  useEffect(() => {
    if (data?.status === "ready" && data.briefing && !data.seen && !seenSent.current) {
      seenSent.current = true;
      briefingApi.markSeen(tripId).catch(() => {});
    }
  }, [data, tripId]);

  if (!data || data.status === "none" || data.status === "failed") return null;

  if (data.status === "pending") {
    return (
      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Sparkles className="h-4 w-4 animate-pulse" /> Researching your trip…
        </div>
        <div className="mt-3 space-y-2">
          <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
          <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  const b = data.briefing;
  if (!b) return null;
  // A briefing with nothing in it is a bar that opens onto nothing.
  const hasContent =
    !!b.whyItMatters ||
    b.eventFacts.length > 0 ||
    b.destination.length > 0 ||
    b.logistics.length > 0;
  if (!hasContent) return null;

  if (data.seen) {
    return (
      <details className="rounded-lg border bg-card">
        <summary className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm">
          <Sparkles className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="font-medium">Trip Briefing</span>
          {/* One line of what is inside, so the closed bar is worth its row. */}
          <span className="min-w-0 truncate text-muted-foreground">
            {b.whyItMatters || b.eventFacts[0]?.text || b.destination[0]?.text || b.logistics[0]?.text}
          </span>
        </summary>
        <div className="border-t px-4 py-3">
          <BriefingBody b={b} />
        </div>
      </details>
    );
  }

  return (
    <section className="rounded-lg border border-primary/30 bg-primary/5 p-4">
      <div className="mb-2 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold tracking-tight">Trip Briefing</h3>
      </div>
      <BriefingBody b={b} />
    </section>
  );
}

function BriefingBody({ b }: { b: BriefingContent }) {
  return (
    <div className="space-y-3 text-sm">
      {b.whyItMatters && <p className="font-medium">{b.whyItMatters}</p>}

      {b.eventFacts.length > 0 && (
        <ul className="space-y-1">
          {b.eventFacts.map((f, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="text-muted-foreground">•</span>
              <span>
                {f.text}{" "}
                {f.sourceUrl && (
                  <a
                    href={f.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center align-middle text-primary hover:underline"
                    aria-label="source"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {b.destination.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Destination</p>
          <ul className="mt-1 space-y-1">
            {b.destination.map((n, i) => (
              <li key={i}>{n.text}</li>
            ))}
          </ul>
        </div>
      )}

      {b.logistics.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Logistics</p>
          <ul className="mt-1 space-y-1">
            {b.logistics.map((n, i) => (
              <li key={i}>{n.text}</li>
            ))}
          </ul>
        </div>
      )}

      {b.sources.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Sources:{" "}
          {b.sources.map((s, i) => (
            <span key={i}>
              {i > 0 && ", "}
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                {s.title || "link"}
              </a>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}
